/**
 * The platform's arc archetype enum and its structural rules (specs/arc-model-v2.md, EAINT-888
 * Phase 1) — the canonical home the `proposal.ts` docblock names. Every arc template declares one
 * of these; the rules below say what stage shape is legal for each and are enforced by
 * `validateArchetype` at template create/update time (`validateArcTemplateShape`).
 *
 * `timeless` and `evolving` describe things that never finish (an organization, a user, a
 * department) — a terminal stage or `arcSettings.autoComplete` on one of these would let the
 * engine declare "done" on something that structurally cannot be. `bounded`, `episode`,
 * `recurring` and `milestone` are all staged state machines and unchanged from today's behaviour.
 */
import type { ArcArchetype } from 'types/arc';

/** Re-exported so `@center-platform/arcs/archetype` stays the import site for the enum's consumers. */
export type { ArcArchetype };

export interface ArchetypeRule {
  /** At least one stage must be declared. */
  stagesRequired: boolean;
  /** A stage may declare `isTerminal: true`. */
  terminalStageAllowed: boolean;
  /** `arcSettings.autoComplete: true` is a legal setting. */
  autoCompleteAllowed: boolean;
}

/**
 * Every archetype in the order an authoring surface lists them: the staged state machines
 * first, then the two that never finish. `archetypeRules` is keyed by the same set.
 */
export const ARC_ARCHETYPES: readonly ArcArchetype[] = [
  'bounded',
  'episode',
  'recurring',
  'milestone',
  'evolving',
  'timeless',
];

export const archetypeRules: Record<ArcArchetype, ArchetypeRule> = {
  timeless: { stagesRequired: false, terminalStageAllowed: false, autoCompleteAllowed: false },
  evolving: { stagesRequired: false, terminalStageAllowed: false, autoCompleteAllowed: false },
  bounded: { stagesRequired: true, terminalStageAllowed: true, autoCompleteAllowed: true },
  episode: { stagesRequired: true, terminalStageAllowed: true, autoCompleteAllowed: true },
  recurring: { stagesRequired: true, terminalStageAllowed: true, autoCompleteAllowed: true },
  milestone: { stagesRequired: true, terminalStageAllowed: true, autoCompleteAllowed: true },
};

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/** Minimal shape this module needs from `ArcTemplateDefinition` — kept local so this package
 *  carries zero fastify/knex/authz imports (see arcs/needs' same constraint, Phase 2). */
interface ArchetypeCheckableDefinition {
  archetype?: ArcArchetype;
  stages?: Array<{ id: string; isTerminal?: boolean }>;
  arcSettings?: { autoComplete?: boolean };
}

/**
 * Validates a definition's `archetype` against its structural rules. Called from
 * `validateArcTemplateShape` at create/update, after the removed-field and automations checks so
 * their error messages are not shadowed by a missing/invalid archetype.
 */
export function validateArchetype(definition: ArchetypeCheckableDefinition): ValidationResult {
  const { archetype } = definition;

  if (!archetype || !(archetype in archetypeRules)) {
    return {
      valid: false,
      error: `'archetype' is required and must be one of: ${Object.keys(archetypeRules).join(', ')}`,
    };
  }

  const rule = archetypeRules[archetype];
  const stages = definition.stages ?? [];

  if (rule.stagesRequired && stages.length === 0) {
    return { valid: false, error: `archetype '${archetype}' requires at least one stage` };
  }

  if (!rule.terminalStageAllowed) {
    const terminalStage = stages.find((stage) => stage.isTerminal === true);
    if (terminalStage) {
      return {
        valid: false,
        error: `archetype '${archetype}' does not allow a terminal stage (stage '${terminalStage.id}')`,
      };
    }
  }

  if (!rule.autoCompleteAllowed && definition.arcSettings?.autoComplete === true) {
    return { valid: false, error: `archetype '${archetype}' does not allow arcSettings.autoComplete` };
  }

  return { valid: true };
}
